import { CardVariant } from "../../models/CardVariant";
import { DeckState } from "../../models/Deck";
import { downloadFiles } from "../../utilities";

export function generateOctgnMissingIdReport(deckState: DeckState): CardVariant[] {
    const variantsWithMissingOctgnIds: CardVariant[] = []

    Object.entries(deckState.forwards).forEach(([_, variant]) => {
        if (!variant.octgnId) {
            variantsWithMissingOctgnIds.push(variant);
        }
    });

    Object.entries(deckState.summons).forEach(([_, variant]) => {
        if (!variant.octgnId) {
            variantsWithMissingOctgnIds.push(variant);
        }
    });

    Object.entries(deckState.monsters).forEach(([_, variant]) => {
        if (!variant.octgnId) {
            variantsWithMissingOctgnIds.push(variant);
        }
    });

    Object.entries(deckState.backups).forEach(([_, variant]) => {
        if (!variant.octgnId) {
            variantsWithMissingOctgnIds.push(variant);
        }
    });

    return variantsWithMissingOctgnIds;
}

function getOctgnMap(variantsById: Record<string, CardVariant>, quantityByVariantId: Record<string, number>) {
    const octgnMap: Record<string, CardVariant & { quantity: number }> = {};
    Object.values(variantsById).forEach((variant) => {
        if (!variant.octgnId) {
            return;
        }

        const existingOrDefault = octgnMap[variant.octgnId] ?? {
            ...variant,
            quantity: 0
        };
        
        const quantity = quantityByVariantId[variant.id] ?? 0;
        existingOrDefault.quantity += quantity;
        octgnMap[variant.octgnId] = existingOrDefault;
    });

    return octgnMap;
}

function populateSection(octgnMap: Record<string, CardVariant & { quantity: number }>, xmlDoc: XMLDocument, element: HTMLElement) {
    Object.entries(octgnMap).forEach(([octgnId, variant]) => {
        const card = xmlDoc.createElement('card');
        card.setAttribute("qty", variant.quantity.toString());
        card.setAttribute("id", variant.octgnId ?? "");
        card.textContent = variant.card.name ?? "";

        element.appendChild(card);
    });
}

export function generateOctgnXml(deckState: DeckState): string {
    const xmlDoc = document.implementation.createDocument(null, '', null);
    var xmlHeader = xmlDoc.createProcessingInstruction('xml', 'version="1.0" encoding="utf-8" standalone="yes"');
    xmlDoc.appendChild(xmlHeader);

    const deck = xmlDoc.createElement("deck");
    deck.setAttribute("game", "285a8cdb-1d02-4edd-a9de-1ade9d5a4f43");

    const forwards = xmlDoc.createElement('section');
    forwards.setAttribute("name", "Forwards");
    forwards.setAttribute("shared", "False");

    const octgnForwardMap = getOctgnMap(deckState.forwards, deckState.editableFields.unsaved.totalCardsByVariant);
    populateSection(octgnForwardMap, xmlDoc, forwards);

    const summons = xmlDoc.createElement('section');
    summons.setAttribute("name", "Summons");
    summons.setAttribute("shared", "False");
    const octgnSummonMap = getOctgnMap(deckState.summons, deckState.editableFields.unsaved.totalCardsByVariant);
    populateSection(octgnSummonMap, xmlDoc, summons);

    const monsters = xmlDoc.createElement('section');
    monsters.setAttribute("name", "Monsters");
    monsters.setAttribute("shared", "False");
    const octgnMonsterMap = getOctgnMap(deckState.monsters, deckState.editableFields.unsaved.totalCardsByVariant);
    populateSection(octgnMonsterMap, xmlDoc, monsters);

    const backups = xmlDoc.createElement('section');
    backups.setAttribute("name", "Backups");
    backups.setAttribute("shared", "False");
    const octgnBackupMap = getOctgnMap(deckState.backups, deckState.editableFields.unsaved.totalCardsByVariant);
    populateSection(octgnBackupMap, xmlDoc, backups);

    const limitBreak = xmlDoc.createElement('section');
    limitBreak.setAttribute("name", "Limit Break");
    limitBreak.setAttribute("shared", "False");
    const octgnLBMap = getOctgnMap(deckState.limitBreak, deckState.editableFields.unsaved.totalCardsByVariant);
    populateSection(octgnLBMap, xmlDoc, limitBreak);

    deck.appendChild(forwards);
    deck.appendChild(summons);
    deck.appendChild(monsters);
    deck.appendChild(backups);
    deck.appendChild(limitBreak);
    xmlDoc.appendChild(deck);

    return new XMLSerializer().serializeToString(xmlDoc);
}

export type ParseOctgnMissingCard = {
    name: string,
    octgnId: string
}

export type ParseOctgnXmlResult = {
    missingCards: ParseOctgnMissingCard[],
    variantQuantityDictionary: Record<string, number>
}

export function parseOctgnXml(text: string, variants: CardVariant[]): ParseOctgnXmlResult {
    const result: ParseOctgnXmlResult = {
        missingCards: [],
        variantQuantityDictionary: {}
    };

    var parser = new DOMParser();
    var xmlDoc = parser.parseFromString(text,"text/xml");

    const cardElements = xmlDoc.querySelectorAll("card");
    cardElements.forEach((cardElement) => {
        const id = cardElement.getAttribute("id");
        const quantityString = cardElement.getAttribute("qty");
        const quantity = parseInt(quantityString || "NaN");

        const matchingVariant = variants.find(x => x.octgnId === id);
    
        if (!matchingVariant || isNaN(quantity)) {
            result.missingCards.push({
                name: cardElement.innerHTML.toString(),
                octgnId: id ?? ""
            });

            return;
        }

        result.variantQuantityDictionary[matchingVariant.id] = quantity;
    });

    return result;
}

export function downloadOctgnXml(deckState: DeckState): void {
    const deckAsXmlString = generateOctgnXml(deckState);
    const date = new Date();
    const deckName = deckState.editableFields.unsaved.name ?? `materiahunter-deck-export-${date}`;
    const filename = `${deckName}.o8d`;
    downloadFiles(deckAsXmlString, filename, ".o8d");
}